import { UseMutationResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  categoriesKeys,
  chatItemsKeys,
  criteriaQuestionsKeys,
  questionContextTranslationsKeys,
  questionCriteriaKeys,
  questionDependenciesKeys,
  questionOptionsKeys,
  questionTranslationsKeys,
  similarQuestionsKeys,
  suggestedQuestionsKeys,
} from '@data/constants';
import {
  HistoryType,
  ListQuestionCategoryView,
  ListQuestionContextTranslationView,
  ListQuestionOptionView,
  ListQuestionTranslationView,
  ListQuestionView,
  QuestionBody,
  QuestionContextTranslationBody,
  QuestionCountListView,
  QuestionCriteriaListView,
  QuestionDependencyBody,
  QuestionDependencyListView,
  QuestionDependencyPreviewBody,
  QuestionDependencyPreviewView,
  QuestionDependencyView,
  QuestionDetailsView,
  QuestionMergeBody,
  QuestionOptionBody,
  QuestionOptionUpdateBody,
  QuestionOptionView,
  QuestionTranslationBody,
  QuestionType,
  QuestionUpdateBody,
  QuestionView,
  QuestionsApiGetQuestionCriteriaRequest,
  QuestionsApiGetQuestionsRequest,
  QuestionsApiGetSuggestedQuestionsRequest,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';
import { Language } from '@data/utils/enums';
import { APIError, DefaultQueryParams } from '@data/utils/types';
import i18n from '../../../app/i18n';

// chat questions (patient app)
export const useCreateChatQuestion = (): UseMutationResult<
  unknown,
  Error,
  { id: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }) => {
      const response = await MatchingApi.questions.retryQuestion({
        id,
        retryQuestionBody: { language: i18n.language as Language },
      });
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: chatItemsKeys.allByHistoryType(HistoryType.All) }),
  });
};

// questions management (back office app)
export const useQuestionDetails = (id: string) =>
  useQuery<QuestionDetailsView, Error>({
    queryKey: criteriaQuestionsKeys.detail(id),
    queryFn: async () => {
      const response = await MatchingApi.questions.getQuestionDetails({ id });
      return response.data;
    },
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });

export const useQuestionTranslations = (id: string) =>
  useQuery<ListQuestionTranslationView, Error>({
    queryKey: questionTranslationsKeys.list(id),
    queryFn: async () => {
      const response = await MatchingApi.questions.getQuestionTranslations({ id });
      return response.data;
    },
    enabled: !!id,
  });

export const useQuestionContextTranslations = (id: string) =>
  useQuery<ListQuestionContextTranslationView, Error>({
    queryKey: questionContextTranslationsKeys.list(id),
    queryFn: async () => {
      const response = await MatchingApi.questions.getQuestionContextTranslations({ id });
      return response.data;
    },
    enabled: !!id,
  });

export const useQuestionDependencies = (id: string) =>
  useQuery<QuestionDependencyListView, Error>({
    queryKey: questionDependenciesKeys.list(id),
    queryFn: async () => {
      const response = await MatchingApi.questions.getQuestionDependencies({ id });
      return response.data;
    },
    enabled: !!id,
  });

export const useCreateQuestionDependency = (): UseMutationResult<
  QuestionDependencyView,
  APIError<QuestionDependencyBody>,
  { id: string; values: QuestionDependencyBody },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.questions.createQuestionDependency({
        id,
        questionDependencyBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: questionDependenciesKeys.list(id) });
      queryClient.invalidateQueries({ queryKey: similarQuestionsKeys.detail(id) });
    },
  });
};

/** Computes what a dependency would derive for a sample source answer, without saving anything. */
export const usePreviewQuestionDependency = (): UseMutationResult<
  QuestionDependencyPreviewView,
  APIError<QuestionDependencyPreviewBody>,
  { id: string; values: QuestionDependencyPreviewBody },
  unknown
> =>
  useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.questions.previewQuestionDependency({
        id,
        questionDependencyPreviewBody: values,
      });
      return response.data;
    },
  });

export const useQuestions = (params: QuestionsApiGetQuestionsRequest, enabled = true) =>
  useQuery<QuestionCountListView, APIError>({
    queryKey: criteriaQuestionsKeys.list(params),
    queryFn: async () => {
      const response = await MatchingApi.questions.getQuestions(params);
      return response.data;
    },
    enabled: !!enabled,
    staleTime: 10 * 60,
  });

export const useCreateQuestion = (): UseMutationResult<
  QuestionView,
  APIError<QuestionBody>,
  QuestionBody,
  unknown
> =>
  useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.questions.createQuestion({
        questionBody: values,
      });
      return response.data;
    },
  });

export const useEditQuestion = (): UseMutationResult<
  QuestionDetailsView,
  APIError<QuestionUpdateBody>,
  { id: string; values: QuestionUpdateBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.questions.updateQuestion({
        id,
        questionUpdateBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: criteriaQuestionsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: similarQuestionsKeys.detail(id) });
    },
  });
};

// edit question criterion
export const useEditQuestionCriterion = (): UseMutationResult<
  QuestionDetailsView,
  APIError<QuestionUpdateBody>,
  { id: string; standardCriterionText: string | null },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, standardCriterionText }) => {
      const response = await MatchingApi.questions.updateQuestion({
        id,
        questionUpdateBody: { standardCriterionText },
      });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: categoriesKeys.question(id) });
      queryClient.invalidateQueries({ queryKey: questionCriteriaKeys.list({ id }) });
    },
  });
};

export const useEditQuestionType = (): UseMutationResult<
  QuestionDetailsView,
  APIError<QuestionUpdateBody>,
  { id: string; values: QuestionUpdateBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.questions.updateQuestion({
        id,
        questionUpdateBody: values,
      });
      return response.data;
    },
    onMutate: async ({ id, values }) => {
      await queryClient.cancelQueries({ queryKey: criteriaQuestionsKeys.detail(id) });

      const previousQuestion = queryClient.getQueryData<QuestionDetailsView>(
        criteriaQuestionsKeys.detail(id),
      );

      if (previousQuestion) {
        const newQuestion = { ...previousQuestion, questionType: values.questionType };

        queryClient.setQueryData(criteriaQuestionsKeys.detail(id), newQuestion);
      }

      return { previousQuestion };
    },
    onError: (_, { id }, context) =>
      queryClient.setQueryData(criteriaQuestionsKeys.detail(id), context?.previousQuestion),
  });
};

export const useDeleteQuestion = (
  invalidate = false,
): UseMutationResult<unknown, APIError<QuestionUpdateBody>, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await MatchingApi.questions.deleteQuestion({ id });
      return response.data;
    },
    onSuccess: () =>
      invalidate
        ? queryClient.invalidateQueries({ queryKey: criteriaQuestionsKeys.all() })
        : undefined,
  });
};

export const useBulkDeleteQuestions = (): UseMutationResult<
  unknown,
  APIError,
  { questionIds: string[] },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.questions.bulkDeleteQuestion({
        questionBulkDelete: values,
      });
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: criteriaQuestionsKeys.all() }),
  });
};

export const useCreateQuestionTranslations = (): UseMutationResult<
  ListQuestionTranslationView,
  APIError<QuestionTranslationBody>,
  { id: string; values: QuestionTranslationBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.questions.createQuestionTranslations({
        id,
        questionTranslationBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { id }) =>
      queryClient.invalidateQueries({ queryKey: questionTranslationsKeys.list(id) }),
  });
};

export const useCreateQuestionContextTranslations = (): UseMutationResult<
  ListQuestionContextTranslationView,
  APIError<QuestionContextTranslationBody>,
  { id: string; values: QuestionContextTranslationBody },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.questions.createQuestionContextTranslations({
        id,
        questionContextTranslationBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { id }) =>
      queryClient.invalidateQueries({ queryKey: questionContextTranslationsKeys.list(id) }),
  });
};

export const useSimilarQuestions = (
  id: string,
  currentVersionOnly: boolean,
  params: DefaultQueryParams,
) =>
  useQuery<ListQuestionView, Error>({
    queryKey: similarQuestionsKeys.detail(id),
    queryFn: async () => {
      const response = await MatchingApi.questions.getSimilarQuestions({
        id,
        currentVersionOnly,
        ...params,
      });
      return response.data;
    },
    enabled: !!id,
  });

export const useMergeQuestions = (): UseMutationResult<
  QuestionDetailsView,
  APIError<QuestionMergeBody>,
  { id: string; values: QuestionMergeBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.questions.mergeQuestions({
        id,
        questionMergeBody: values,
      });
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: criteriaQuestionsKeys.allForType([QuestionType.Proposed]),
      }),
  });
};

export const useOptimisticMergeQuestionWithSuggestedQuestion = (): UseMutationResult<
  QuestionDetailsView,
  APIError<QuestionMergeBody>,
  {
    id: string;
    suggestedQuestionsParams: QuestionsApiGetSuggestedQuestionsRequest;
    values: QuestionMergeBody;
  },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.questions.mergeQuestions({
        id,
        questionMergeBody: values,
      });
      return response.data;
    },
    onMutate: async ({ id, suggestedQuestionsParams }) => {
      await queryClient.cancelQueries({
        queryKey: suggestedQuestionsKeys.list(suggestedQuestionsParams),
      });

      const suggestedQuestions = queryClient.getQueryData<ListQuestionView>(
        suggestedQuestionsKeys.list(suggestedQuestionsParams),
      );

      const newSuggestedQuestions = suggestedQuestions?.items.filter(
        (question) => question.id !== id,
      );

      queryClient.setQueryData(suggestedQuestionsKeys.list(suggestedQuestionsParams), {
        ...suggestedQuestions,
        items: newSuggestedQuestions,
      });

      return { suggestedQuestions };
    },
    onError: (_, { suggestedQuestionsParams }, context) =>
      queryClient.setQueryData(
        suggestedQuestionsKeys.list(suggestedQuestionsParams),
        context?.suggestedQuestions,
      ),
    onSettled: (_, __, { suggestedQuestionsParams }) => {
      queryClient.invalidateQueries({
        queryKey: suggestedQuestionsKeys.list(suggestedQuestionsParams),
      });
    },
  });
};

export const useOptimisticMergeQuestionWithSimilarQuestion = (): UseMutationResult<
  QuestionDetailsView,
  APIError<QuestionMergeBody>,
  {
    id: string;
    values: QuestionMergeBody;
  },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.questions.mergeQuestions({
        id,
        questionMergeBody: values,
      });
      return response.data;
    },

    onMutate: async ({ id, values: { targetQuestionId } }) => {
      await queryClient.cancelQueries({
        queryKey: similarQuestionsKeys.detail(targetQuestionId),
      });

      const similarQuestions = queryClient.getQueryData<ListQuestionView>(
        similarQuestionsKeys.detail(targetQuestionId),
      );

      const newSimilarQuestions = similarQuestions?.items.filter((question) => question.id !== id);

      queryClient.setQueryData(similarQuestionsKeys.detail(targetQuestionId), {
        ...similarQuestions,
        items: newSimilarQuestions,
      });

      return { similarQuestions };
    },
    onError: (_, { values: { targetQuestionId } }, context) =>
      queryClient.setQueryData(
        similarQuestionsKeys.detail(targetQuestionId),
        context?.similarQuestions,
      ),
    onSettled: (_, __, { values: { targetQuestionId } }) => {
      queryClient.invalidateQueries({ queryKey: similarQuestionsKeys.detail(targetQuestionId) });
    },
  });
};

export const useQuestionsOnMutate = (): UseMutationResult<
  QuestionCountListView,
  APIError,
  { params: QuestionsApiGetQuestionsRequest },
  unknown
> => {
  return useMutation({
    mutationFn: async ({ params }) => {
      const response = await MatchingApi.questions.getQuestions(params);
      return response.data;
    },
  });
};

export const useQuestionOptions = (id: string, params: DefaultQueryParams, enabled = true) =>
  useQuery<ListQuestionOptionView, Error>({
    queryKey: questionOptionsKeys.list(id),
    queryFn: async () => {
      const response = await MatchingApi.questions.getQuestionOptions({ id, ...params });
      return response.data;
    },
    enabled: !!id && !!enabled,
  });

export const useEditQuestionOption = (): UseMutationResult<
  QuestionOptionView,
  APIError,
  { optionId: string; questionId: string; values: QuestionOptionUpdateBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ optionId, questionId, values }) => {
      const response = await MatchingApi.questions.updateQuestionOption({
        optionId,
        questionId,
        questionOptionUpdateBody: values,
      });
      return response.data;
    },

    onSuccess: (_, { questionId }) =>
      queryClient.invalidateQueries({ queryKey: questionOptionsKeys.list(questionId) }),
  });
};

export const useCreateQuestionOption = (): UseMutationResult<
  QuestionOptionView,
  APIError,
  { questionId: string; values: QuestionOptionBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ questionId, values }) => {
      const response = await MatchingApi.questions.addQuestionOption({
        id: questionId,
        questionOptionBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { questionId }) =>
      queryClient.invalidateQueries({ queryKey: questionOptionsKeys.list(questionId) }),
  });
};

export const useRemoveQuestionOption = (): UseMutationResult<
  unknown,
  APIError,
  { optionId: string; questionId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ questionId, optionId }) => {
      const response = await MatchingApi.questions.deleteQuestionOption({ optionId, questionId });
      return response.data;
    },
    onSuccess: (_, { questionId }) =>
      queryClient.invalidateQueries({ queryKey: questionOptionsKeys.list(questionId) }),
  });
};

// Assign category to question
export const useAssignCategoryToQuestion = (): UseMutationResult<
  void,
  APIError,
  { categoryId: string; id: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, categoryId }) => {
      const response = await MatchingApi.questions.addQuestionCategory({
        id,
        addQuestionCategoryBody: { categoryId },
      });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: criteriaQuestionsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: categoriesKeys.questionList({ id }) });
    },
  });
};

export const useQuestionCriteria = (params: QuestionsApiGetQuestionCriteriaRequest) =>
  useQuery<QuestionCriteriaListView, Error>({
    queryKey: questionCriteriaKeys.list(params),
    queryFn: async () => {
      const response = await MatchingApi.questions.getQuestionCriteria(params);
      return response.data;
    },
    enabled: !!params.id,
  });

export const useRemoveQuestionCategory = (): UseMutationResult<
  void,
  APIError,
  { categoryId: string; questionId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ questionId, categoryId }) => {
      await MatchingApi.questions.removeQuestionCategory({
        id: questionId,
        removeQuestionCategoryBody: { categoryId },
      });
    },
    onMutate: async ({ questionId, categoryId }) => {
      await queryClient.cancelQueries({ queryKey: categoriesKeys.question(questionId) });

      const previousCategories = queryClient.getQueryData<ListQuestionCategoryView>(
        categoriesKeys.question(questionId),
      );

      if (previousCategories) {
        queryClient.setQueryData(categoriesKeys.question(questionId), {
          ...previousCategories,
          items: previousCategories.items.filter((category) => category.categoryId !== categoryId),
        });
      }

      return { previousCategories };
    },
    onError: (err, { questionId }, context) => {
      queryClient.setQueryData(categoriesKeys.question(questionId), context?.previousCategories);
    },
    onSettled: (_, __, { questionId }) => {
      queryClient.invalidateQueries({ queryKey: categoriesKeys.questionList({ id: questionId }) });
    },
  });
};

export const useSuggestedQuestions = (
  id: string,
  params: Omit<QuestionsApiGetSuggestedQuestionsRequest, 'id'>,
) =>
  useQuery<ListQuestionView, Error>({
    queryKey: suggestedQuestionsKeys.list({ id, ...params }),
    queryFn: async () => {
      const response = await MatchingApi.questions.getSuggestedQuestions({ id, ...params });
      return response.data;
    },
    enabled: !!id,
  });

export const useIgnoreMerge = (): UseMutationResult<
  void,
  APIError,
  {
    id: string;
    secondQuestionId: string;
    suggestedQuestionsParams: QuestionsApiGetSuggestedQuestionsRequest;
  },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, secondQuestionId }) => {
      const response = await MatchingApi.questions.ignoreMerge({
        id,
        questionIgnoreMergeBody: {
          secondQuestionId,
        },
      });

      return response.data;
    },
    onMutate: async ({ id, suggestedQuestionsParams }) => {
      await queryClient.cancelQueries({
        queryKey: suggestedQuestionsKeys.list(suggestedQuestionsParams),
      });

      const suggestedQuestions = queryClient.getQueryData<ListQuestionView>(
        suggestedQuestionsKeys.list(suggestedQuestionsParams),
      );

      const newSuggestedQuestions = suggestedQuestions?.items.filter(
        (question) => question.id !== id,
      );

      queryClient.setQueryData(suggestedQuestionsKeys.list(suggestedQuestionsParams), {
        ...suggestedQuestions,
        items: newSuggestedQuestions,
      });

      return { suggestedQuestions };
    },
    onError: (_, { suggestedQuestionsParams }, context) =>
      queryClient.setQueryData(
        suggestedQuestionsKeys.list(suggestedQuestionsParams),
        context?.suggestedQuestions,
      ),
    onSettled: (_, __, { suggestedQuestionsParams }) => {
      queryClient.invalidateQueries({
        queryKey: suggestedQuestionsKeys.list(suggestedQuestionsParams),
      });
    },
  });
};
